import { useState } from 'react'
import {
  addAiTier,
  removeAiTier,
  reorderAiTiers,
  saveAiCompany,
  saveAiTier,
  type AiAgentShell,
  type AiTier,
} from '../../api/settingsAiAgent'
import { failed, LabModelPick, NoteText, supportsFast, type GlobalDraft, type Note } from './parts'
import { UsagePanel } from './UsagePanel'
import './AgentModelsTab.css'

/**
 * Fila "Agent Models".
 *
 * La GLOBAL: pornit/oprit, si treptele (Deep / Balanced / Quick + cele proprii)
 * cu laboratorul si modelul fiecareia. Radio-ul alege treapta implicita - cea
 * pe care o ia o firma care nu si-a pus alta.
 *
 * La GENERAL: ce alege FIRMA - treptele platformei sau un model propriu,
 * treapta ei si bugetul de investigatie. Cheia si modelul raman in Global.
 *
 * ORDINEA TREPTELOR nu e o toana de ecran: lista asta e chiar selectorul din
 * sertarul agentului, la fiecare intrebare. De aia se salveaza singura, cu
 * sagetile - un buton Save in plus, pentru o sageata, ar fi doar un al doilea
 * pas de uitat.
 */
export function AgentModelsTab({
  page,
  draft,
  onDraft,
  onSaveGlobal,
  onReload,
}: {
  page: AiAgentShell
  draft: GlobalDraft
  onDraft(next: GlobalDraft): void
  onSaveGlobal(): void
  onReload(): void
}) {
  const isCompany = page.scope === 'company'

  return (
    <div className="aia-setup-grid">
      <div className="aia-setup-main">
        {isCompany ? (
          <CompanyPanel page={page} onReload={onReload} />
        ) : (
          <GlobalPanel page={page} draft={draft} onDraft={onDraft} onSaveGlobal={onSaveGlobal} onReload={onReload} />
        )}
      </div>
      <UsagePanel page={page} />
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  GLOBAL
 * ------------------------------------------------------------------ */

function GlobalPanel({
  page,
  draft,
  onDraft,
  onSaveGlobal,
  onReload,
}: {
  page: AiAgentShell
  draft: GlobalDraft
  onDraft(next: GlobalDraft): void
  onSaveGlobal(): void
  onReload(): void
}) {
  const [order, setOrder] = useState<string[]>(Object.keys(page.tiers))
  const [note, setNote] = useState<Note | null>(null)

  const tiers = order.filter((key) => page.tiers[key]).map((key) => [key, page.tiers[key]] as const)

  const move = (key: string, delta: number) => {
    const at = order.indexOf(key)
    const to = at + delta
    if (at < 0 || to < 0 || to >= order.length) return

    const next = [...order]
    next.splice(at, 1)
    next.splice(to, 0, key)
    setOrder(next)

    void reorderAiTiers(next)
      .then((r) => setNote(r.ok ? { text: 'order saved' } : { text: r.error ?? 'Could not save the order', bad: true }))
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const defaultLab = page.aiLabs.find((l) => l.id === (page.tiers[draft.effort]?.lab_id ?? 0)) ?? null
  const defaultTierModel = (page.tiers[draft.effort]?.model ?? '').trim()
  const ready =
    draft.enabled &&
    (page.ai.has_key || (page.tiers[draft.effort]?.has_key ?? false) || (defaultLab?.has_key ?? false)) &&
    (page.ai.model !== '' || defaultTierModel !== '')

  return (
    <div className="panel panel-default" id="odai-global">
      <div className="panel-body">
        <div className="aia-row">
          <label className="aia-row-label">Agent</label>
          <div className="aia-row-field">
            <label className="aia-switch">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => onDraft({ ...draft, enabled: e.target.checked })}
              />
              <span>{draft.enabled ? 'the agent is on' : 'the agent is off'}</span>
            </label>
            <span className="aia-help">
              The working mode (Problem / Change / Incident / Full Access) is switched per company under{' '}
              <a href="/app/change">Change Proposals</a>.
            </span>
          </div>
        </div>

        <div className="aia-row">
          <label className="aia-row-label">Tiers</label>
          <div className="aia-row-field">
            <table className="aia-tiers">
              <tbody>
                {tiers.map(([key, tier]) => (
                  <TierRow
                    key={key}
                    page={page}
                    slug={key}
                    tier={tier}
                    isDefaultChoice={draft.effort === key}
                    onPickDefault={() => onDraft({ ...draft, effort: key })}
                    onMove={(delta) => move(key, delta)}
                    onRemoved={onReload}
                  />
                ))}
              </tbody>
            </table>
            <span className="aia-help">
              the arrows set the order people see in the agent&apos;s tier picker. The <strong>default</strong> box
              marks the tier a company gets unless it picks its own. Labs and their models are managed under AI Labs
              &amp; Models. <NoteText note={note} />
              {!page.tiersReady ? <strong> Needs the 2026_09_04_100006 and 100007 migrations.</strong> : null}
            </span>

            <AddTier page={page} onAdded={onReload} />
          </div>
        </div>

        <div className="aia-actions">
          <button type="button" className="btn btn-primary" onClick={onSaveGlobal}>
            Save
          </button>
          <span className={ready ? 'aia-ready is-ok' : 'aia-ready'}>
            {ready
              ? 'ready - the OptimumDesk AI icon in the bar opens the drawer'
              : !draft.enabled
                ? 'turn the agent on to use it'
                : !page.ai.has_key && !(page.tiers[draft.effort]?.has_key ?? false) && !(defaultLab?.has_key ?? false)
                  ? 'no lab with a key yet - add one under AI Labs & Models'
                  : 'no model on the default tier yet - pick one above'}
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * Un rand de treapta: numele si adancimea se scriu la ORICE treapta, si la cele
 * trei din cod - "Deep", "Balanced", "Quick" sunt numele NOASTRE, si se vad in
 * selectorul sertarului la fiecare intrebare. Gol inseamna "cel din cod".
 */
function TierRow({
  page,
  slug,
  tier,
  isDefaultChoice,
  onPickDefault,
  onMove,
  onRemoved,
}: {
  page: AiAgentShell
  slug: string
  tier: AiTier
  isDefaultChoice: boolean
  onPickDefault(): void
  onMove(delta: number): void
  onRemoved(): void
}) {
  const [label, setLabel] = useState(tier.label)
  const [effort, setEffort] = useState(tier.effort)
  const [labId, setLabId] = useState(tier.lab_id)
  const [model, setModel] = useState(tier.model)
  const [fast, setFast] = useState(tier.speed === 'fast')
  const [note, setNote] = useState<Note | null>(null)

  const provider = page.aiLabs.find((l) => l.id === labId)?.provider ?? 'anthropic'
  const fastOk = supportsFast(provider, model)

  const save = () => {
    setNote({ text: '…' })
    void saveAiTier({
      tier: slug,
      lab_id: labId,
      model,
      speed: fast && fastOk ? 'fast' : 'standard',
      label,
      effort,
    })
      .then((r) => setNote(r.ok ? { text: 'Saved.' } : { text: r.error ?? 'Could not save.', bad: true }))
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const remove = () => {
    if (!window.confirm('Remove this tier? Companies and people on it fall back to the default tier.')) return

    void removeAiTier(slug)
      .then((r) => (r.ok ? onRemoved() : setNote({ text: 'Could not remove', bad: true })))
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  return (
    <tr className={tier.is_default ? 'aia-tier-row' : 'aia-tier-row is-custom'}>
      <td className="aia-tier-cell-move">
        <span className="aia-tier-arrows">
          <button type="button" className="aia-tier-move" aria-label="move up" onClick={() => onMove(-1)}>
            ▲
          </button>
          <button type="button" className="aia-tier-move" aria-label="move down" onClick={() => onMove(1)}>
            ▼
          </button>
        </span>
      </td>
      <td className="aia-tier-cell-name">
        <input
          type="text"
          className="form-control input-sm"
          value={label}
          placeholder={tier.is_default ? tier.label : 'tier name'}
          onChange={(e) => setLabel(e.target.value)}
        />
        <div className="aia-help">
          reasoning:{' '}
          <select
            className="input-sm"
            style={{ fontSize: 12 }}
            value={effort}
            onChange={(e) => setEffort(e.target.value)}
          >
            {page.efforts.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
          {tier.is_default && tier.hint ? <span title={tier.hint}> · {tier.hint}</span> : null}
        </div>
      </td>
      <td colSpan={2}>
        <LabModelPick
          labs={page.aiLabs}
          labId={labId}
          model={model}
          onChange={(next) => {
            setLabId(next.labId)
            setModel(next.model)
          }}
        />
      </td>
      <td className="aia-tier-cell-fast">
        <label
          className="aia-switch"
          title="fast mode: up to 2.5x faster output at double the token price - only on models that have it"
        >
          <input type="checkbox" checked={fast && fastOk} disabled={!fastOk} onChange={(e) => setFast(e.target.checked)} />
          <span className="aia-help">fast</span>
        </label>
      </td>
      <td className="aia-tier-cell-default">
        <label className="aia-switch" title="the tier a company gets unless it picks its own">
          <input type="radio" name="odai-effort" checked={isDefaultChoice} onChange={onPickDefault} />
          <span className="aia-help">default</span>
        </label>
      </td>
      <td className="aia-tier-cell-save">
        <button type="button" className="btn btn-default btn-sm" disabled={!page.tiersReady} onClick={save}>
          Save
        </button>
        {!tier.is_default ? (
          <button type="button" className="btn btn-link btn-sm" style={{ color: '#b42318' }} onClick={remove}>
            Remove
          </button>
        ) : null}
        <NoteText note={note} />
      </td>
    </tr>
  )
}

/** O treapta noua, pe langa cele trei: nume, laborator, model, adancime, fast. */
function AddTier({ page, onAdded }: { page: AiAgentShell; onAdded(): void }) {
  const [label, setLabel] = useState('')
  const [labId, setLabId] = useState(0)
  const [model, setModel] = useState('')
  const [effort, setEffort] = useState('high')
  const [fast, setFast] = useState(false)
  const [note, setNote] = useState<Note | null>(null)

  const provider = page.aiLabs.find((l) => l.id === labId)?.provider ?? 'anthropic'
  const fastOk = supportsFast(provider, model)

  const add = () => {
    setNote({ text: '…' })
    void addAiTier({ label, lab_id: labId, model, effort, speed: fast && fastOk ? 'fast' : 'standard' })
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Failed', bad: true })
          return
        }
        setNote({ text: `Added (${r.tier})` })
        setLabel('')
        onAdded()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  return (
    <details className="aia-tier-add">
      <summary>Add tier</summary>
      <div className="aia-tier-add-body">
        <div>
          <label className="aia-help">Name</label>
          <input
            type="text"
            className="form-control input-sm"
            placeholder="Cheap night runs"
            maxLength={60}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <div>
          <label className="aia-help">AI lab and model</label>
          <LabModelPick
            labs={page.aiLabs}
            labId={labId}
            model={model}
            onChange={(next) => {
              setLabId(next.labId)
              setModel(next.model)
            }}
          />
        </div>
        <div>
          <label className="aia-help">Reasoning</label>
          <select className="form-control input-sm" value={effort} onChange={(e) => setEffort(e.target.value)}>
            {page.efforts.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="aia-switch" style={{ marginTop: 20 }}>
            <input type="checkbox" disabled={!fastOk} checked={fast && fastOk} onChange={(e) => setFast(e.target.checked)} />{' '}
            <span className="aia-help">fast</span>
          </label>
        </div>
        <div>
          <button type="button" className="btn btn-primary btn-sm" disabled={!page.customTiersReady} onClick={add}>
            Add tier
          </button>{' '}
          <NoteText note={note} />
        </div>
      </div>
      {!page.customTiersReady ? (
        <div className="aia-help" style={{ padding: '0 12px 10px' }}>
          Custom tiers need the <code>2026_09_05_100004_ai_tiers_custom_labs_catalog</code> migration.
        </div>
      ) : null}
    </details>
  )
}

/* ------------------------------------------------------------------ *
 *  GENERAL - ce alege FIRMA
 * ------------------------------------------------------------------ */

function CompanyPanel({ page, onReload }: { page: AiAgentShell; onReload(): void }) {
  const [source, setSource] = useState(page.company.source === 'own' ? 'own' : 'default')
  const [labId, setLabId] = useState(page.company.lab_id)
  const [model, setModel] = useState(page.company.model)
  const [effort, setEffort] = useState(page.company.effort)
  const [steps, setSteps] = useState(page.company.max_steps)
  const [note, setNote] = useState<Note | null>(null)

  const save = () => {
    setNote({ text: 'Saving…' })
    void saveAiCompany({
      source,
      effort,
      max_steps: steps,
      lab_id: labId,
      model,
      /* Cheia firmei nu se scrie de pe ecranul asta: laboratorul o are. */
      api_key: '',
    })
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Could not save.', bad: true })
          return
        }
        setNote({ text: 'Saved.' })
        onReload()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const globalTierLabel = page.tiers[page.ai.effort]?.label ?? page.ai.effort

  return (
    <div className="panel panel-default" id="odai-company">
      <div className="panel-body">
        <div className="aia-row">
          <label className="aia-row-label">Model</label>
          <div className="aia-row-field">
            {/* Clase proprii, fara `radio-inline`: vezi AgentModelsTab.css. */}
            <div className="aia-choices">
              <label className="aia-choice">
                <input
                  className="aia-choice-input"
                  type="radio"
                  name="odai-source"
                  checked={source !== 'own'}
                  onChange={() => setSource('default')}
                />
                <span>the platform&apos;s tiers</span>
              </label>
              <label className="aia-choice">
                <input
                  className="aia-choice-input"
                  type="radio"
                  name="odai-source"
                  checked={source === 'own'}
                  onChange={() => setSource('own')}
                />
                <span>this company&apos;s own model</span>
              </label>
            </div>
            <span className="aia-help">
              own = a lab and a model from the platform&apos;s registry; the tiers then do not apply to this company.
            </span>
            {source === 'own' ? (
              <div className="odai-company-own" style={{ marginTop: 8 }}>
                <div className="aia-pair">
                  <LabModelPick
                    labs={page.aiLabs}
                    labId={labId}
                    model={model}
                    onChange={(next) => {
                      setLabId(next.labId)
                      setModel(next.model)
                    }}
                  />
                </div>
                {page.aiLabs.length === 0 ? (
                  <span className="aia-help">
                    no lab in the registry yet - the platform owner adds one under AI Labs &amp; Models.
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="aia-row">
          <label className="aia-row-label">Tier</label>
          <div className="aia-row-field">
            <select
              className="form-control input-sm"
              style={{ width: 'auto' }}
              value={effort}
              onChange={(e) => setEffort(e.target.value)}
            >
              <option value="">As Global ({globalTierLabel})</option>
              {Object.entries(page.tiers).map(([key, tier]) => (
                <option key={key} value={key}>
                  {tier.label}
                  {tier.model !== '' ? ' - ' + tier.model : ''}
                </option>
              ))}
            </select>
            <span className="aia-help">
              Deep, Balanced, Quick or a custom tier - the model behind each is set by Global. People can still pick
              another tier from the drawer.
            </span>
          </div>
        </div>

        <div className="aia-row">
          <label className="aia-row-label">Investigation budget</label>
          <div className="aia-row-field">
            <input
              type="number"
              min={0}
              max={2048}
              className="form-control input-sm aia-num"
              value={steps}
              placeholder={String(page.ai.max_steps)}
              onChange={(e) => setSteps(Number(e.target.value))}
            />
            <span className="aia-help">0 = as Global ({page.ai.max_steps} rounds).</span>
          </div>
        </div>

        <div className="aia-actions">
          <button type="button" className="btn btn-primary" onClick={save}>
            Save for {page.companyName}
          </button>
          <NoteText note={note} />
        </div>
      </div>
    </div>
  )
}
